<script setup>
import { format } from "date-fns";

const {
  program,
  label = "Talk to our Student Ambassadors",
  university,
} = defineProps({
  program: Object,
  label: String,
  university: Object,
});

const router = useRouter();
const route = useRoute();

const toast = useToast();
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const { data: sessionData, status } = useAuth();

const isAuthenticated = computed(() => {
  return status.value === "authenticated";
});

const step = ref(1);
const visible = ref(false);
const consultationNotAllowedDialog = ref(false);
const consultDate = ref(null);
const consultTimeLabel = ref(null);
const consultTime = ref(null);
const preferredSubject = ref(program?.name ?? null);
const preferredDegree = ref(getAttributeData("degree") ?? null);
const studentNote = ref("");
const preferredDegrees = ref([
  { label: "Bachelor", value: "Bachelor" },
  { label: "Master", value: "Master" },
  { label: "PhD", value: "PhD" },
]);

const dialogHeader = computed(() => {
  if (step.value === 1) {
    return "Schedule an appointment";
  } else if (step.value === 2) {
    return "Add your preferred subject";
  } else if (step.value === 3) {
    return "Review your reservation details";
  } else {
    return "Review your reservation details";
  }
});

const scheduleTimes = computed(() => {
  const timesArray = [];
  let hour = 8; // Start from 8 AM
  let minute = 0;

  while (hour < 21 || minute === 0) {
    // Label format: HH:MM AM/PM
    const period = hour < 12 ? "AM" : "PM";
    const formattedHourLabel = hour % 12 === 0 ? 12 : hour % 12;
    const formattedMinuteLabel = minute.toString().padStart(2, "0");
    const label = `${formattedHourLabel}:${formattedMinuteLabel} ${period}`;

    // Value format: HH:MM:00 (24-hour format)
    const formattedHourValue = hour.toString().padStart(2, "0");
    const formattedMinuteValue = minute.toString().padStart(2, "0");
    const value = `${formattedHourValue}:${formattedMinuteValue}:00`;

    // Push to array
    timesArray.push({ label, value });

    // Increment 30 minutes
    minute += 30;
    if (minute >= 60) {
      minute = 0;
      hour += 1;
    }
  }
  return timesArray;
});

const closeConsult = () => {
  visible.value = false;
  step.value = 1;
  consultDate.value = null;
  consultTimeLabel.value = null;
  consultTime.value = null;
  preferredSubject.value = program?.name || null;
};

const updateTime = (time) => {
  consultTimeLabel.value = time.label;
  consultTime.value = time.value;
};

const reserveSchedule = async () => {
  await useApiFetch("student-applications", {
    method: "POST",
    body: {
      university_id: program?.university?.id || university?.id,
      program_id: program?.id,
      degree: getAttributeData("degree") || null,
      meeting_time:
        format(consultDate.value, "yyyy-MM-dd") + " " + consultTime.value,
      meeting_timezone: timezone,
      preferred_subject: preferredSubject.value,
      preferred_degree: preferredDegree.value,
      student_note: studentNote.value,
    },
    onResponse({ response }) {
      if ([200, 201].includes(response.status)) {
        step.value = 4;
      }
    },
    onResponseError() {
      const errorMsg = response._data?.errors?.at(0) || response._data?.message;
      toast.add({
        severity: "error",
        summary: errorMsg,
        life: 3000,
      });
      closeConsult();
    },
  });
};

const isUniversityConsultable = computed(() => {
  return university?.is_consultable || program?.university?.is_consultable;
});

const onClick = (event) => {
  if (!isAuthenticated.value) return;

  const hasCompletedProfile =
    sessionData?.value?.data?.student_profile_completed;

  if (!isUniversityConsultable.value) {
    consultationNotAllowedDialog.value = true;
    return;
  } else if (isUniversityConsultable.value && hasCompletedProfile) {
    visible.value = true;
    return;
  }
};

function getAttributeData(key) {
  const attribute = program?.attributes?.data?.find((d) => d.key === key);
  return attribute ? attribute.value : "";
}

const isDisabled = () => {
  if (!consultTime.value || !consultDate.value) return true;

  // Get current date/time and add 24 hours
  const currentDate = new Date();
  const minBookingTime = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);

  // Create datetime from selected date and time
  const selectedDateTime = new Date(
    format(consultDate.value, "yyyy-MM-dd") + " " + consultTime.value,
  );

  // Disable if selected time is less than 6 hours from now
  return selectedDateTime < minBookingTime;
};

const isTimeDisabled = (timeValue) => {
  if (!consultDate.value) return false;

  // Add 6 hours to current time for minimum booking window
  const currentDate = new Date();
  currentDate.setHours(currentDate.getHours() + 24);

  const selectedDateTime = new Date(
    `${format(consultDate.value, "yyyy-MM-dd")}T${timeValue}`,
  );

  return selectedDateTime < currentDate;
};

// Add this ref for the time slots container
const timeContainer = ref(null);

// Add this function to scroll to first available time slot
const scrollToFirstAvailableSlot = () => {
  if (!timeContainer.value || !consultDate.value) return;

  nextTick(() => {
    const firstAvailableButton = timeContainer.value.querySelector(
      "button:not([disabled])",
    );
    if (firstAvailableButton) {
      firstAvailableButton.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  });
};

const handleScheduleClick = () => {
  if (program) {
    step.value = 3;
  } else {
    step.value = 2;
  }
};

const closeConsultationNotAllowedDialog = () => {
  consultationNotAllowedDialog.value = false;
};

const goToExplore = () => {
  if (route.query?.from == "explore") {
    router.back();
    return;
  }

  navigateTo("/explore");
};

// Watch for date changes to trigger scrolling
watch(
  consultDate,
  () => {
    scrollToFirstAvailableSlot();
  },
  { immediate: false },
);
</script>

<template>
  <div class="">
    <ProfileCompleteGuard
      :isOnlyRequireLogin="false"
      :isDisabled="!isUniversityConsultable && isAuthenticated"
      @onCallback="onClick"
    >
      <Button
        icon="pi pi-angle-right"
        iconPos="right"
        severity="tertiary"
        :label="label"
        @click="onClick"
      >
      </Button>
    </ProfileCompleteGuard>

    <Dialog
      v-model:visible="consultationNotAllowedDialog"
      modal
      header="&nbsp;"
      @close="closeConsultationNotAllowedDialog"
      class="w-[30rem] m-2 rounded-2xl flex justify-center pb-4"
      :pt="{
        header: 'p-3 py-0 flex items-center justify-between',
        headerActions: 'fixed right-3 top-3',
      }"
    >
      <!-- Image/Icon -->
      <div class="flex justify-center mb-4">
        <img
          src="~/assets/img/consult-disabled.svg"
          alt="Consultation Unavailable"
          class="w-28 h-28 mt-4"
        />
      </div>
      <h3 class="text-lg font-semibold text-center mb-4">
        Consultation is not available
      </h3>
      <p class="text-center text-gray-600 text-sm px-4">
        We’re sorry to inform you that the university you booked isn’t available
        for consultation yet. In the meantime, let’s explore other great
        universities and programs together!
      </p>
      <template #footer>
        <div class="flex !justify-center w-full">
          <Button
            class="rounded-lg min-w-24 !px-8"
            label="Explore Other Programs"
            :pt="{
              label: 'text-sm',
            }"
            @click="goToExplore"
          ></Button>
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="visible"
      modal
      @close="closeConsult"
      :pt="{
        header:
          'p-5 flex items-center justify-between border-b border-slate-200',
        content: 'p-0 overflow-auto',
        footer: 'px-8 !py-5 flex justify-end gap-2 border-t border-slate-200',
      }"
      class="w-auto"
      :show-header="step !== 3"
    >
      <template #header>
        <p class="text-xl text-primary-600 font-semibold">{{ dialogHeader }}</p>
      </template>
      <div
        v-if="step === 1"
        class="flex flex-col md:flex-row gap-2 md:w-[950px] max-h-[95vh]"
      >
        <div class="p-5 bg-surface-50 lg:w-1/4 w-full">
          <p class="text-surface-700 font-semibold text-lg mb-8">
            Consultation details
          </p>
          <p class="text-dark font-medium">
            {{ program?.university?.name || university?.name }}
          </p>
          <p v-if="program?.name" class="text-surface-800 text-sm">
            {{ program?.name }}
          </p>
        </div>
        <div class="p-5 lg:w-3/4 w-full">
          <p class="text-surface-700 font-semibold text-lg mb-8">
            Select date & time
          </p>
          <div class="flex flex-col lg:flex-row gap-5">
            <div class="w-full lg:w-auto">
              <DatePicker
                v-model="consultDate"
                inline
                :minDate="
                  new Date(new Date().setDate(new Date().getDate() + 1))
                "
                class="w-full lg:min-w-[450px] lg:min-h-[400px]"
                dateFormat="yy/mm/dd"
                :pt="{
                  panel: 'border-0',
                  panel: 'w-full h-full',
                  // daycell: 'px-2 py-4',
                  // day: 'p-4',
                }"
              />
            </div>
            <div class="space-y-1 w-full lg:w-auto">
              <p class="text-sm font-semibold text-surface-700 mb-5">
                {{ format(consultDate, "EEEE, MMMM d") || "Select Date" }}
              </p>
              <div
                class="h-96 overflow-auto px-2 space-y-1.5"
                ref="timeContainer"
              >
                <div v-for="time in scheduleTimes">
                  <Button
                    :outlined="time.value !== consultTime"
                    :label="time.label"
                    class="w-full lg:w-48 block rounded-xl !border-slate-300"
                    :disabled="isTimeDisabled(time.value)"
                    :pt="{
                      label: 'font-semibold text-sm',
                    }"
                    @click="updateTime(time)"
                  ></Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="step === 2"
        class="text-center mx-auto space-y-3 md:w-[480px] w-full"
      >
        <div class="text-left space-y-4 px-6 pt-4">
          <div>
            <Label>Preferred Subject</Label>
            <InputText
              v-model="preferredSubject"
              name="preferred_subject"
              placeholder="e.g Computer Science"
              class="placeholder:text-xs lg:placeholder:text-sm mt-2 text-[#4B5563] text-xs lg:text-sm"
              fluid
            />
          </div>
          <div>
            <Label>Preferred Degree</Label>
            <Select
              v-model="preferredDegree"
              :options="preferredDegrees || []"
              optionLabel="label"
              optionValue="value"
              placeholder="Select degree"
              class="placeholder:text-xs py-[2px] lg:placeholder:text-sm mt-2 text-[#4B5563] text-xs lg:text-sm"
              fluid
            />
          </div>
          <div>
            <label class="text-sm font-medium text-surface-700"
              >Notes (Optional)</label
            >
            <Textarea
              v-model="studentNote"
              class="w-full mt-2 text-xs lg:text-sm"
              placeholder="Anything you'd like us to know?"
            />
          </div>
        </div>
        <div class="flex justify-end px-6 pt-6 pb-4 gap-2">
          <Button
            label="Back"
            outlined
            class="rounded-xl min-w-24"
            :pt="{ label: 'text-sm' }"
            @click="step = 1"
          />
          <Button
            label="Continue"
            class="rounded-xl min-w-24"
            :pt="{ label: 'text-sm' }"
            @click="step = 3"
          />
        </div>
      </div>

      <div
        v-else-if="step === 3"
        class="text-center mx-auto pt-5 pb-10 px-8 md:w-[480px] w-full"
      >
        <div class="flex justify-end">
          <Button
            icon="pi pi-times"
            text
            rounded
            @click="closeConsult"
          ></Button>
        </div>
        <div>
          <i class="pi pi-calendar-clock !text-5xl text-primary-600 mb-8"></i>
          <p class="text-lg font-semibold text-slate-700">
            {{ format(consultDate, "EEEE, MMMM d") }}, {{ consultTimeLabel }}
          </p>
        </div>
        <Button
          label="Change Schedule"
          link
          @click="step = 1"
          class="text-sky-500 text-sm"
        />
        <div class="px-6 py-3 bg-slate-100 rounded-xl mb-8 mt-3">
          <p class="text-sm text-black font-medium">
            {{ program?.university?.name || university?.name }}
          </p>
          <p v-if="program?.name" class="text-xs text-slate-800">
            {{ program?.name }}
          </p>
        </div>
        <Button
          class="rounded-xl min-w-36"
          label="Reserved now"
          :pt="{
            label: 'text-sm',
          }"
          @click="reserveSchedule"
        ></Button>
      </div>
      <div
        v-else-if="step === 4"
        class="text-center mx-auto space-y-3 md:w-[480px] w-full"
      >
        <img
          src="~/assets/img/reserved.png"
          alt="celebrate"
          class="mx-auto rounded-t-lg"
        />
        <div class="pt-2 pb-10 px-8">
          <div class="my-6 space-y-3">
            <p class="text-xl text-primary-600 font-semibold">
              Successfully reserved
            </p>
            <p class="max-w-[500px] m-auto">
              Once we’ve matched you with a student mentor, we’ll schedule your
              consultation and share the details. This process typically takes
              up to 12 hours maximum. You’ll receive confirmation and reminder
              emails at
              <span class="text-blue-500">{{ sessionData?.data?.email }}</span>
            </p>
          </div>
          <Button
            class="rounded-xl min-w-24"
            label="Got it"
            :pt="{
              label: 'text-sm',
            }"
            @click="closeConsult"
          ></Button>
        </div>
      </div>

      <template #footer v-if="step === 1">
        <Button
          outlined
          class="rounded-xl min-w-24"
          label="Cancel"
          :pt="{
            label: 'text-primary-500 font-semibold text-sm',
          }"
          @click="closeConsult"
        ></Button>
        <Button
          class="rounded-xl min-w-24"
          label="Continue"
          :disabled="isDisabled()"
          :pt="{
            label: 'text-sm',
          }"
          @click="handleScheduleClick"
        ></Button>
      </template>
    </Dialog>
  </div>
</template>
